import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TechLayout from '../../components/tech/TechLayout';
import {
  getWorkOrder,
  getPartRequests,
  createPartRequest,
  updatePartRequest,
  deletePartRequest,
} from '../../api/client';

const STATUS_BG = {
  pending: '#FEF3C7',
  ordered: '#DBEAFE',
  shipped: '#EDE9FE',
  delivered: '#D1FAE5',
};

const STATUS_COLOR = {
  pending: '#92400E',
  ordered: '#1E40AF',
  shipped: '#5B21B6',
  delivered: '#065F46',
};

function StatusPill({ status }) {
  return (
    <span
      style={{
        background: STATUS_BG[status] || '#F3F4F6',
        color: STATUS_COLOR[status] || '#374151',
        padding: '4px 10px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
      }}
    >
      {status || 'unknown'}
    </span>
  );
}

export default function TechJobParts() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [partReqs, setPartReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const [form, setForm] = useState({
    part_name: '',
    part_code: '',
    quantity: 1,
    tracking_number: '',
    status: 'pending',
  });

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    part_name: '',
    part_code: '',
    quantity: 1,
    tracking_number: '',
    status: 'pending',
  });

  const load = async () => {
    try {
      setLoading(true);

      const [jobRes, prRes] = await Promise.all([
        getWorkOrder(id),
        getPartRequests(id),
      ]);

      setJob(jobRes.data);
      setPartReqs(prRes.data || []);
    } catch (err) {
      console.error('load parts page failed:', err.response?.data || err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const handleCreate = async () => {
    if (!form.part_name.trim()) {
      setMessage('Part name is required');
      setTimeout(() => setMessage(''), 2000);
      return;
    }

    try {
      setSaving(true);
      await createPartRequest(id, {
        ...form,
        quantity: Number(form.quantity) || 1,
      });

      setForm({
        part_name: '',
        part_code: '',
        quantity: 1,
        tracking_number: '',
        status: 'pending',
      });

      setMessage('Part request submitted');
      await load();
    } catch (err) {
      console.error('create part request failed:', err.response?.data || err);
      setMessage('Submit failed');
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 2000);
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditForm({
      part_name: row.part_name || '',
      part_code: row.part_code || '',
      quantity: row.quantity || 1,
      tracking_number: row.tracking_number || '',
      status: row.status || 'pending',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({
      part_name: '',
      part_code: '',
      quantity: 1,
      tracking_number: '',
      status: 'pending',
    });
  };

  const handleSaveEdit = async (prId) => {
    try {
      setSaving(true);
      await updatePartRequest(id, prId, {
        ...editForm,
        quantity: Number(editForm.quantity) || 1,
      });

      setMessage('Part request updated');
      cancelEdit();
      await load();
    } catch (err) {
      console.error('update part request failed:', err.response?.data || err);
      setMessage('Update failed');
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 2000);
    }
  };

  const handleDelete = async (prId) => {
    try {
      setSaving(true);
      await deletePartRequest(id, prId);
      setMessage('Part request deleted');
      await load();
    } catch (err) {
      console.error('delete part request failed:', err.response?.data || err);
      setMessage('Delete failed');
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 2000);
    }
  };

  return (
    <TechLayout title="Parts Request">
      <button
        onClick={() => navigate(`/tech/jobs/${id}`)}
        style={{
          marginBottom: 14,
          border: 'none',
          background: '#F3F4F6',
          padding: '8px 12px',
          borderRadius: 8,
          cursor: 'pointer',
          fontWeight: 600,
        }}
      >
        ← Back
      </button>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading...</div>
      ) : (
        <>
          <div
            style={{
              background: '#fff',
              border: '1px solid #E5E7EB',
              borderRadius: 12,
              padding: 16,
              marginBottom: 14,
            }}
          >
            <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>
              Job
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>
              {job?.job_number || `WO-${job?.id}`}
            </div>
            <div style={{ fontSize: 14, color: '#374151', marginTop: 2 }}>
              {job?.customer_name || 'Unknown Customer'}
            </div>
          </div>

          <div
            style={{
              background: '#fff',
              border: '1px solid #E5E7EB',
              borderRadius: 12,
              padding: 16,
              marginBottom: 14,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 12 }}>
              New Part Request
            </div>

            <Field label="Part Name">
              <input
                value={form.part_name}
                onChange={(e) => setForm({ ...form, part_name: e.target.value })}
                style={inputStyle}
                placeholder="e.g. Water pump"
              />
            </Field>

            <Field label="Part Code">
              <input
                value={form.part_code}
                onChange={(e) => setForm({ ...form, part_code: e.target.value })}
                style={inputStyle}
                placeholder="e.g. W0201043"
              />
            </Field>

            <Field label="Quantity">
              <input
                type="number"
                min={1}
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                style={inputStyle}
              />
            </Field>

            <button
              onClick={handleCreate}
              disabled={saving}
              style={{
                width: '100%',
                marginTop: 6,
                border: 'none',
                background: '#2563EB',
                color: '#fff',
                borderRadius: 10,
                padding: '12px 14px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {saving ? 'Submitting...' : 'Submit Request'}
            </button>

            {message && (
              <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: '#059669' }}>
                {message}
              </div>
            )}
          </div>

          <div
            style={{
              background: '#fff',
              border: '1px solid #E5E7EB',
              borderRadius: 12,
              padding: 16,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 12 }}>
              Existing Requests
            </div>

            {partReqs.length === 0 ? (
              <div style={{ fontSize: 14, color: '#6B7280' }}>No part requests yet.</div>
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                {partReqs.map((row) => (
                  <div
                    key={row.id}
                    style={{
                      border: '1px solid #E5E7EB',
                      borderRadius: 12,
                      padding: 14,
                      background: '#F9FAFB',
                    }}
                  >
                    {editingId === row.id ? (
                      <>
                        <Field label="Part Name">
                          <input
                            value={editForm.part_name}
                            onChange={(e) => setEditForm({ ...editForm, part_name: e.target.value })}
                            style={inputStyle}
                          />
                        </Field>

                        <Field label="Part Code">
                          <input
                            value={editForm.part_code}
                            onChange={(e) => setEditForm({ ...editForm, part_code: e.target.value })}
                            style={inputStyle}
                          />
                        </Field>

                        <Field label="Quantity">
                          <input
                            type="number"
                            min={1}
                            value={editForm.quantity}
                            onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })}
                            style={inputStyle}
                          />
                        </Field>

                        <Field label="Tracking Number">
                          <input
                            value={editForm.tracking_number}
                            onChange={(e) => setEditForm({ ...editForm, tracking_number: e.target.value })}
                            style={inputStyle}
                          />
                        </Field>

                        <Field label="Status">
                          <select
                            value={editForm.status}
                            onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                            style={inputStyle}
                          >
                            <option value="pending">pending</option>
                            <option value="ordered">ordered</option>
                            <option value="shipped">shipped</option>
                            <option value="delivered">delivered</option>
                          </select>
                        </Field>

                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button
                            onClick={() => handleSaveEdit(row.id)}
                            style={primaryBtn}
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            style={secondaryBtn}
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                            {row.part_name || 'Unnamed Part'}
                          </div>
                          <StatusPill status={row.status} />
                        </div>

                        <Info label="Part Code" value={row.part_code} />
                        <Info label="Quantity" value={row.quantity} />
                        <Info label="Tracking Number" value={row.tracking_number} />

                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                          <button onClick={() => startEdit(row)} style={secondaryBtn}>
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(row.id)}
                            style={{
                              ...secondaryBtn,
                              background: '#FEE2E2',
                              color: '#DC2626',
                              border: '1px solid #FECACA',
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </TechLayout>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          fontSize: 11,
          color: '#6B7280',
          fontWeight: 700,
          textTransform: 'uppercase',
          marginBottom: 5,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, color: '#111827' }}>{value || '—'}</div>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #D1D5DB',
  fontSize: 14,
  boxSizing: 'border-box',
};

const primaryBtn = {
  flex: 1,
  border: 'none',
  background: '#2563EB',
  color: '#fff',
  borderRadius: 8,
  padding: '10px 12px',
  fontWeight: 700,
  cursor: 'pointer',
};

const secondaryBtn = {
  flex: 1,
  border: '1px solid #D1D5DB',
  background: '#fff',
  color: '#374151',
  borderRadius: 8,
  padding: '10px 12px',
  fontWeight: 700,
  cursor: 'pointer',
};